import { useState, useEffect, useRef } from 'react';
import { api } from '../App';
import Navigation from '../components/Navigation';
import { MessageCircle, Send, Image, Video, Mic, Check, CheckCheck, X, CreditCard, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

// Helper to get full image URL (handles relative and absolute URLs)
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
};

const Trainer = ({ user, logout }) => {
  const [trainers, setTrainers] = useState([]);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [activeConversation, setActiveConversation] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [messageForm, setMessageForm] = useState({
    trainer_id: '',
    subject: '',
    message: ''
  });
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [pricingPlans, setPricingPlans] = useState([]);
  const [couponCode, setCouponCode] = useState('');
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadTrainers();
    loadConversations();
    checkSubscription();
    loadPricingPlans();
    
    // Check for subscription success in URL
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('subscription') === 'success') {
      const plan = urlParams.get('plan');
      toast.success(`Subscription activated successfully! You can now chat with trainers.`);
      // Clean up URL
      window.history.replaceState({}, '', '/trainer');
      // Reload subscription
      setTimeout(() => checkSubscription(), 500);
    }
  }, []);

  // Track previous message count to only scroll on new messages
  const prevMessageCountRef = useRef(0);
  
  useEffect(() => {
    // Only scroll if new messages were added
    if (messages.length > prevMessageCountRef.current) {
      scrollToBottom();
    }
    prevMessageCountRef.current = messages.length;
  }, [messages]);

  // Poll for new messages every 5 seconds (reduced frequency)
  useEffect(() => {
    if (activeConversation) {
      const interval = setInterval(() => {
        loadMessages(activeConversation.id);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [activeConversation]);

  // Check subscription status every minute
  useEffect(() => {
    const interval = setInterval(() => {
      checkSubscription();
    }, 60000); // Check every 60 seconds
    return () => clearInterval(interval);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadTrainers = async () => {
    try {
      const response = await api.get('/trainers');
      setTrainers(response.data);
    } catch (error) {
      console.error('Error loading trainers:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPricingPlans = async () => {
    try {
      const response = await api.get('/pricing-plans');
      setPricingPlans(response.data);
    } catch (error) {
      console.error('Error loading pricing plans:', error);
    }
  };

  const loadConversations = async () => {
    try {
      const response = await api.get('/conversations');
      console.log('[CONVERSATIONS] Loaded conversations:', response.data);
      console.log('[CONVERSATIONS] Conversation IDs:', response.data.map(c => c.id));
      setConversations(response.data);
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  };

  const deleteConversation = async (conversationId, trainerName) => {
    console.log('[DELETE] Attempting to delete conversation:', conversationId, 'with trainer:', trainerName);
    
    // Use toast for confirmation on mobile
    toast(
      `Delete conversation with ${trainerName}?`,
      {
        description: 'This will delete all messages. This action cannot be undone.',
        action: {
          label: 'Delete',
          onClick: async () => {
            try {
              console.log('[DELETE] Sending delete request for conversation:', conversationId);
              const response = await api.delete(`/conversations/${conversationId}`);
              console.log('[DELETE] Delete response:', response.data);
              toast.success(`Conversation deleted (${response.data.deleted_messages} messages removed)`);
              
              // Clear active conversation if it was the deleted one
              if (activeConversation?.id === conversationId) {
                setActiveConversation(null);
                setMessages([]);
              }
              
              // Refresh conversations list
              await loadConversations();
            } catch (error) {
              console.error('[DELETE] Error deleting conversation:', error);
              console.error('[DELETE] Error response:', error.response?.data);
              const errorDetail = error.response?.data?.detail || 'Failed to delete conversation';
              toast.error(`Delete failed: ${errorDetail}`);
            }
          }
        },
        cancel: {
          label: 'Cancel',
          onClick: () => {}
        },
        duration: 10000,
      }
    );
  };

  const checkSubscription = async () => {
    try {
      const response = await api.get('/subscriptions/active');
      setSubscription(response.data);
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const startConversation = async (trainer) => {
    if (!subscription) {
      setSelectedTrainer(trainer);
      setShowPaymentModal(true);
      return;
    }

    try {
      const response = await api.post('/conversations', { trainer_id: trainer.id });
      setActiveConversation({ ...response.data, trainer });
      await loadMessages(response.data.id);
      await loadConversations();
    } catch (error) {
      console.error('Error starting conversation:', error);
      toast.error('Failed to start conversation');
    }
  };

  const loadMessages = async (conversationId) => {
    try {
      const response = await api.get(`/messages/${conversationId}`);
      setMessages(response.data);
      
      // Mark messages as read
      const unreadMessages = response.data.filter(m => m.sender_type === 'trainer' && !m.is_read);
      if (unreadMessages.length > 0) {
        await api.post('/messages/mark-read', {
          message_ids: unreadMessages.map(m => m.id)
        });
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !activeConversation) return;

    try {
      await api.post('/messages', {
        conversation_id: activeConversation.id,
        trainer_id: activeConversation.trainer_id,
        message_type: 'text',
        content: newMessage
      });
      
      setNewMessage('');
      await loadMessages(activeConversation.id);
      await checkSubscription(); // Refresh subscription to update message count
    } catch (error) {
      console.error('Error sending message:', error);
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail);
        setShowPaymentModal(true);
      } else {
        toast.error('Failed to send message');
      }
    }
  };

  const handleFileUpload = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/upload-media', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      await api.post('/messages', {
        conversation_id: activeConversation.id,
        trainer_id: activeConversation.trainer_id,
        message_type: type,
        content: response.data.url
      });

      await loadMessages(activeConversation.id);
      await checkSubscription();
      toast.success(`${type} uploaded successfully`);
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    }
  };

  const startVoiceRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = async () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const file = new File([blob], 'voice-message.webm', { type: 'audio/webm' });
        
        const formData = new FormData();
        formData.append('file', file);
        
        try {
          const response = await api.post('/upload-media', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });

          await api.post('/messages', {
            conversation_id: activeConversation.id,
            trainer_id: activeConversation.trainer_id,
            message_type: 'voice',
            content: response.data.url
          });

          await loadMessages(activeConversation.id);
          await checkSubscription();
          toast.success('Voice message sent');
        } catch (error) {
          console.error('Error sending voice message:', error);
          toast.error('Failed to send voice message');
        }

        stream.getTracks().forEach(track => track.stop());
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Failed to start recording');
    }
  };

  const stopVoiceRecording = () => {
    if (mediaRecorder) {
      mediaRecorder.stop();
      setIsRecording(false);
      setMediaRecorder(null);
    }
  };

  const getSubscriptionStatus = () => {
    if (!subscription || !subscription.valid_until) return null;
    
    const now = new Date();
    const validUntil = new Date(subscription.valid_until);
    const diffTime = validUntil - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    const isExpired = diffDays <= 0;
    const isExpiringSoon = diffDays > 0 && diffDays <= 2;
    
    return {
      daysRemaining: diffDays,
      expirationDate: validUntil,
      isExpired,
      isExpiringSoon,
      planType: subscription.plan_type
    };
  };

  const handlePayment = async (planType) => {
    console.log('[PAYMENT] handlePayment called with planType:', planType);
    
    if (!planType) {
      toast.error('Please select a plan first');
      return;
    }
    
    setSelectedPlan(planType);
    
    // Check if it's pay per message plan
    if (planType === 'per_message') {
      // Close payment modal and show message dialog
      setShowPaymentModal(false);
      setShowMessageDialog(true);
      // Pre-select trainer if one was already selected
      setMessageForm({
        trainer_id: selectedTrainer?.id || '',
        subject: '',
        message: ''
      });
      return;
    }
    
    // For subscription plans, proceed with Stripe checkout
    setProcessingPayment(true);

    try {
      // Ensure we're using HTTPS for the origin URL
      const originUrl = window.location.protocol === 'https:' 
        ? window.location.origin 
        : `https://${window.location.host}`;
      
      console.log('[PAYMENT] Using origin URL:', originUrl);
      console.log('[PAYMENT] Sending request to create-checkout...');
      
      const response = await api.post('/subscriptions/create-checkout', {
        plan_type: planType,
        origin_url: originUrl,
        coupon_code: couponCode || undefined
      });

      console.log('[PAYMENT] Stripe checkout response:', response.data);
      
      if (!response.data.url) {
        throw new Error('No checkout URL returned from server');
      }
      
      // Ensure the Stripe URL is HTTPS
      let checkoutUrl = response.data.url;
      if (checkoutUrl.startsWith('http://')) {
        checkoutUrl = checkoutUrl.replace('http://', 'https://');
      }

      console.log('[PAYMENT] Redirecting to:', checkoutUrl);
      
      // Redirect to Stripe checkout
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('[PAYMENT] Error creating checkout:', error);
      console.error('[PAYMENT] Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to create checkout session. Please try again.');
      setProcessingPayment(false);
    }
  };

  const handleSendPayPerMessage = async () => {
    // Validate form
    if (!messageForm.trainer_id) {
      toast.error('Please select a trainer');
      return;
    }
    if (!messageForm.subject || !messageForm.message) {
      toast.error('Please fill in subject and message');
      return;
    }

    try {
      setProcessingPayment(true);
      
      // Send the message via API (creates conversation and first message)
      const response = await api.post('/trainer/send-pay-per-message', {
        trainer_id: messageForm.trainer_id,
        subject: messageForm.subject,
        message: messageForm.message
      });

      toast.success('Message sent! You can now chat with your trainer.');
      
      // Close dialog and reset form
      setShowMessageDialog(false);
      setMessageForm({
        trainer_id: '',
        subject: '',
        message: ''
      });
      
      // Select the trainer and load the conversation
      const trainer = trainers.find(t => t.id === messageForm.trainer_id);
      if (trainer) {
        setSelectedTrainer(trainer);
        await loadConversations();
      }
    } catch (error) {
      console.error('Error sending message:', error);
      if (error.response?.status === 403) {
        toast.error('You need to pay for each message. Please purchase again.');
        // Show payment modal again
        setShowMessageDialog(false);
        setShowPaymentModal(true);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to send message');
      }
    } finally {
      setProcessingPayment(false);
    }
  };

  const renderMessageContent = (message) => {
    if (message.message_type === 'text') {
      return <p className="break-words">{message.content}</p>;
    } else if (message.message_type === 'image') {
      return <img src={`${process.env.REACT_APP_BACKEND_URL}${message.content}`} alt="Shared" className="max-w-xs rounded" />;
    } else if (message.message_type === 'video') {
      return <video src={`${process.env.REACT_APP_BACKEND_URL}${message.content}`} controls className="max-w-xs rounded" />;
    } else if (message.message_type === 'voice') {
      return <audio src={`${process.env.REACT_APP_BACKEND_URL}${message.content}`} controls className="max-w-xs" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#FCFCF8' }}>
        <Navigation user={user} logout={logout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-t-transparent rounded-full animate-spin mx-auto" style={{ borderColor: '#6FCFD3', borderTopColor: 'transparent' }}></div>
            <p className="mt-4" style={{ color: '#372F2F' }}>Loading trainers...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FCFCF8' }}>
      <Navigation user={user} logout={logout} />
      
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-4xl font-bold mb-2" style={{ color: '#D66238' }}>Ask a Trainer</h1>
        <p className="mb-6" style={{ color: '#372F2F' }}>Get expert advice from professional mustang trainers</p>

        {/* Subscription Status with Real-Time Tracker */}
        {subscription && (() => {
          const status = getSubscriptionStatus();
          if (!status) return null;
          
          const bgColor = status.isExpired 
            ? '#D66238' 
            : status.isExpiringSoon 
            ? '#F59E0B' 
            : '#6FCFD3';
          
          return (
            <div className="mb-6 p-4 rounded-lg shadow-lg" style={{ backgroundColor: bgColor, color: 'white' }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-bold text-lg">
                    {subscription.plan_type === 'weekly_unlimited' && 'Weekly Unlimited'}
                    {subscription.plan_type === 'monthly_unlimited' && 'Monthly Unlimited'}
                    {subscription.plan_type === 'per_message' && 'Pay Per Message'}
                  </p>
                  {subscription.plan_type === 'per_message' ? (
                    <p className="text-sm">Messages remaining: {subscription.messages_remaining || 0}</p>
                  ) : status.isExpired ? (
                    <p className="text-sm font-semibold">⚠️ EXPIRED - Please renew to continue messaging</p>
                  ) : status.isExpiringSoon ? (
                    <p className="text-sm font-semibold">⏰ Expiring soon! {status.daysRemaining} day{status.daysRemaining !== 1 ? 's' : ''} remaining</p>
                  ) : (
                    <p className="text-sm">{status.daysRemaining} day{status.daysRemaining !== 1 ? 's' : ''} remaining</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-xs opacity-90">Expires on</p>
                  <p className="font-semibold">{status.expirationDate.toLocaleDateString()}</p>
                  {status.isExpired && (
                    <button
                      onClick={() => setShowPaymentModal(true)}
                      className="mt-2 px-3 py-1 bg-white rounded text-sm font-semibold hover:opacity-90 transition"
                      style={{ color: bgColor }}
                    >
                      Renew Now
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Trainers & Conversations List */}
          <div className="lg:col-span-1 space-y-4">
            {/* Active Conversations */}
            {conversations.length > 0 && (
              <div className="bg-white rounded-lg shadow p-4">
                <h2 className="font-bold mb-4" style={{ color: '#D66238' }}>Your Conversations <span className="text-xs text-gray-400">(v3)</span></h2>
                {conversations.map((conv) => (
                  <div
                    key={conv.id}
                    className="p-3 mb-2 rounded border border-gray-200"
                    style={{ 
                      borderLeft: `4px solid ${activeConversation?.id === conv.id ? '#6FCFD3' : '#ccc'}`,
                      backgroundColor: activeConversation?.id === conv.id ? '#f0fffe' : 'white'
                    }}
                  >
                    <div className="flex items-center justify-between">
                      {/* Trainer info - clickable */}
                      <div 
                        className="flex items-center gap-2 flex-1 cursor-pointer"
                        onClick={() => {
                          setActiveConversation(conv);
                          loadMessages(conv.id);
                        }}
                      >
                        {conv.trainer?.photo_url ? (
                          <img src={getImageUrl(conv.trainer.photo_url)} alt={conv.trainer.name} className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#6FCFD3' }}>
                            <span className="text-white font-bold">{conv.trainer?.name?.charAt(0)}</span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold" style={{ color: '#372F2F' }}>{conv.trainer?.name}</p>
                          <p className="text-sm text-gray-500 truncate">{conv.last_message || 'No messages yet'}</p>
                        </div>
                        {conv.unread_count_user > 0 && (
                          <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">{conv.unread_count_user}</span>
                        )}
                      </div>
                      {/* Delete button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteConversation(conv.id, conv.trainer?.name || 'Trainer');
                        }}
                        className="ml-2 px-2 py-1 bg-red-500 hover:bg-red-600 text-white text-xs rounded flex items-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" />
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Available Trainers */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="font-bold mb-4" style={{ color: '#D66238' }}>Available Trainers</h2>
              {trainers.map((trainer) => (
                <div key={trainer.id} className="p-4 mb-4 border rounded hover:shadow-md transition">
                  <div className="flex items-start gap-3">
                    {trainer.photo_url ? (
                      <img src={getImageUrl(trainer.photo_url)} alt={trainer.name} className="w-16 h-16 rounded-full object-cover" />
                    ) : (
                      <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: '#6FCFD3' }}>
                        <span className="text-white text-xl font-bold">{trainer.name.charAt(0)}</span>
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="font-bold" style={{ color: '#372F2F' }}>{trainer.name}</h3>
                      {(trainer.city || trainer.state) && (
                        <p className="text-sm text-gray-500 mb-1">
                          {trainer.city}{trainer.city && trainer.state ? ', ' : ''}{trainer.state}
                        </p>
                      )}
                      <p className="text-sm text-gray-600 mb-2">{trainer.bio}</p>
                      <div className="flex flex-wrap gap-1 mb-2">
                        {trainer.specialties.map((specialty, idx) => (
                          <span key={idx} className="text-xs px-2 py-1 rounded" style={{ backgroundColor: '#6FCFD3', color: 'white' }}>
                            {specialty}
                          </span>
                        ))}
                      </div>
                      <p className="text-sm mb-1" style={{ color: '#D66238' }}>Status: {trainer.availability}</p>
                      {trainer.response_time && (
                        <p className="text-xs mb-2 text-gray-600">Response time: {trainer.response_time}</p>
                      )}
                      <button
                        onClick={() => startConversation(trainer)}
                        className="px-4 py-2 rounded text-white hover:opacity-90 transition"
                        style={{ backgroundColor: '#6FCFD3' }}
                      >
                        <MessageCircle className="inline mr-2" size={16} />
                        Message
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Messaging Area */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow">
            {activeConversation ? (
              <div className="flex flex-col h-[600px]">
                {/* Chat Header */}
                <div className="p-4 border-b flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {activeConversation.trainer?.photo_url ? (
                      <img src={getImageUrl(activeConversation.trainer.photo_url)} alt={activeConversation.trainer.name} className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#6FCFD3' }}>
                        <span className="text-white text-xl font-bold">{activeConversation.trainer?.name?.charAt(0)}</span>
                      </div>
                    )}
                    <div>
                      <h3 className="font-bold" style={{ color: '#372F2F' }}>{activeConversation.trainer?.name}</h3>
                      <p className="text-sm text-gray-500">{activeConversation.trainer?.availability}</p>
                    </div>
                  </div>
                  <button onClick={() => setActiveConversation(null)} className="text-gray-500 hover:text-gray-700">
                    <X size={20} />
                  </button>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className="max-w-[70%] p-3 rounded-lg"
                        style={{
                          backgroundColor: message.sender_type === 'user' ? '#6FCFD3' : '#f3f4f6',
                          color: message.sender_type === 'user' ? 'white' : '#372F2F'
                        }}
                      >
                        {renderMessageContent(message)}
                        <div className="flex items-center gap-1 mt-1 text-xs opacity-70">
                          <span>{new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                          {message.sender_type === 'user' && (
                            message.is_read ? <CheckCheck size={14} /> : <Check size={14} />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <div className="p-4 border-t">
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => handleFileUpload(e, 'image')}
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="p-2 rounded hover:bg-gray-100"
                      style={{ color: '#6FCFD3' }}
                    >
                      <Image size={20} />
                    </button>
                    <button
                      onClick={isRecording ? stopVoiceRecording : startVoiceRecording}
                      className={`p-2 rounded ${isRecording ? 'bg-red-500 text-white' : 'hover:bg-gray-100'}`}
                      style={{ color: isRecording ? 'white' : '#6FCFD3' }}
                    >
                      <Mic size={20} />
                    </button>
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                      placeholder="Type a message..."
                      className="flex-1 p-2 border rounded focus:outline-none focus:border-[#6FCFD3]"
                    />
                    <button
                      onClick={sendMessage}
                      className="p-2 rounded text-white"
                      style={{ backgroundColor: '#6FCFD3' }}
                    >
                      <Send size={20} />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-[600px] flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <MessageCircle size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Select a conversation or start a new one</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-md w-full my-8 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl sm:text-2xl font-bold mb-3" style={{ color: '#D66238' }}>Choose Your Plan</h2>
            <p className="mb-4 text-sm text-gray-600">Select a subscription plan to start messaging trainers</p>
            
            <div className="space-y-3">
              {pricingPlans.length === 0 ? (
                <p className="text-center text-gray-500 text-sm">Loading pricing plans...</p>
              ) : (
                pricingPlans
                  .filter(plan => ['per_message', 'weekly_unlimited', 'trainer_monthly'].includes(plan.plan_type))
                  .map((plan) => (
                  <button
                    key={plan.id}
                    onClick={() => setSelectedPlan(plan.plan_type)}
                    disabled={processingPayment}
                    className="w-full p-3 border-2 rounded-lg hover:shadow-md transition text-left"
                    style={{ 
                      borderColor: selectedPlan === plan.plan_type ? '#D66238' : '#6FCFD3',
                      backgroundColor: selectedPlan === plan.plan_type ? '#fff5f2' : 'white'
                    }}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-bold text-sm sm:text-base" style={{ color: '#372F2F' }}>{plan.name}</p>
                        <p className="text-xs sm:text-sm text-gray-600">{plan.description}</p>
                      </div>
                      <p className="text-xl sm:text-2xl font-bold" style={{ color: '#D66238' }}>${plan.price}</p>
                    </div>
                  </button>
                ))
              )}
            </div>

            {/* Coupon Code Input */}
            <div className="mt-4">
              <label className="block text-xs sm:text-sm font-semibold mb-2" style={{ color: '#D66238' }}>
                Have a coupon code?
              </label>
              <input
                type="text"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder="Enter coupon code"
                className="w-full p-2 text-sm border-2 rounded-lg focus:outline-none focus:border-teal-500"
                style={{ borderColor: '#6FCFD3' }}
                disabled={processingPayment}
              />
            </div>

            {/* Payment Button Section */}
            <div className="flex flex-col gap-2 pt-4 mt-4 border-t">
              <button
                onClick={() => handlePayment(selectedPlan)}
                disabled={processingPayment || !selectedPlan}
                className="w-full p-3 rounded-lg text-white font-bold text-sm sm:text-base"
                style={{
                  backgroundColor: selectedPlan ? '#D66238' : '#ccc',
                  cursor: selectedPlan ? 'pointer' : 'not-allowed',
                  minHeight: '48px'
                }}
              >
                {processingPayment ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2 inline-block"></div>
                    Processing...
                  </>
                ) : (
                  `💳 SUBSCRIBE NOW${selectedPlan ? ` - $${pricingPlans.find(p => p.plan_type === selectedPlan)?.price || ''}` : ''}`
                )}
              </button>
              
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setSelectedPlan(null);
                  setCouponCode('');
                }}
                disabled={processingPayment}
                className="w-full p-2 border rounded-lg hover:bg-gray-50 font-medium text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pay Per Message Dialog */}
      {showMessageDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#D66238' }}>Send Message to Trainer</h2>
            <p className="mb-6 text-gray-600">Fill in the form below to send your question to a trainer. They will respond via email.</p>
            
            <div className="space-y-4">
              {/* Trainer Selection */}
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#D66238' }}>
                  Select Trainer *
                </label>
                <select
                  value={messageForm.trainer_id}
                  onChange={(e) => setMessageForm({ ...messageForm, trainer_id: e.target.value })}
                  className="w-full p-3 border-2 rounded-lg focus:outline-none focus:border-teal-500"
                  style={{ borderColor: '#6FCFD3' }}
                >
                  <option value="">Choose a trainer...</option>
                  {trainers.map((trainer) => (
                    <option key={trainer.id} value={trainer.id}>
                      {trainer.name} - {trainer.specialties?.join(', ')}
                    </option>
                  ))}
                </select>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#D66238' }}>
                  Subject *
                </label>
                <input
                  type="text"
                  value={messageForm.subject}
                  onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })}
                  placeholder="Enter subject line"
                  className="w-full p-3 border-2 rounded-lg focus:outline-none focus:border-teal-500"
                  style={{ borderColor: '#6FCFD3' }}
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#D66238' }}>
                  Your Question/Message *
                </label>
                <textarea
                  value={messageForm.message}
                  onChange={(e) => setMessageForm({ ...messageForm, message: e.target.value })}
                  placeholder="Type your question or message here..."
                  rows={6}
                  className="w-full p-3 border-2 rounded-lg focus:outline-none focus:border-teal-500 resize-none"
                  style={{ borderColor: '#6FCFD3' }}
                />
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowMessageDialog(false);
                  setMessageForm({ trainer_id: '', subject: '', message: '' });
                }}
                disabled={processingPayment}
                className="flex-1 p-3 border-2 rounded-lg hover:bg-gray-50 transition"
                style={{ borderColor: '#D66238', color: '#D66238' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSendPayPerMessage}
                disabled={processingPayment}
                className="flex-1 p-3 rounded-lg text-white font-semibold hover:opacity-90 transition flex items-center justify-center gap-2"
                style={{ backgroundColor: '#6FCFD3' }}
              >
                {processingPayment ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Send Message
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Trainer;
